import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authApi } from "../services/auth";

const AuthContext = createContext({ user: null, loading: true });

export function AuthProvider({ children }) {
  // Three states: null = checking, object = authenticated, false = guest
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      // Use /bootstrap (always 200) for the initial probe so anonymous
      // visitors don't trigger a 401 entry in the browser console.
      const res = await authApi.bootstrap();
      setUser(res?.user ?? false);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth callback, skip /me check.
    // AuthCallback will exchange session_id and establish the cookie first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const u = await authApi.login(email, password);
    setUser(u);
    return u;
  };

  const register = async (email, password, name, persona) => {
    const u = await authApi.register(email, password, name, persona);
    setUser(u);
    return u;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch {}
    setUser(false);
  };

  const value = { user, loading, login, register, logout, refresh: checkAuth };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
